import { Typography } from '@material-ui/core'
import { useEffect } from 'react'

import { isolate } from '../../../../helpers/bidi'
import { formatPromotionMinorAmount } from '../../../../helpers/promotionAmount'
import { getTranslate } from '../../../../localization'
import { CartItem, PromotionIntent } from '../../../../types/cart'
import { ValidatedPromotionCode } from '../../promotionReservationIntent'
import AppliedOffer from './AppliedOffer'
import CodeEntry from './CodeEntry'
import OfferList from './OfferList'
import PromotionVerdict from './PromotionVerdict'
import PropertyDiscounts from './PropertyDiscounts'
import useStyles from './styles/index.styles'
import { useAdvertisedOffers } from './useAdvertisedOffers'
import { usePromotionEvaluation } from './usePromotionEvaluation'
import { usePromotionIntent } from './usePromotionIntent'
import { usePropertyDiscounts } from './usePropertyDiscounts'

type PromotionSectionProps = {
  intent: PromotionIntent
  setIntent: React.Dispatch<React.SetStateAction<PromotionIntent>>
  cartID: string
  /** Selected cart lines turn evaluator line IDs into booker-facing labels. */
  cartItems?: CartItem[]
  disabled?: boolean
  cartKey?: string
  onValidatedCodeChange?: (validated?: ValidatedPromotionCode) => void
}

/**
 * Discounts, on the CART page.
 *
 * It lives here rather than on Traveller & Payment because `promotionIntent` is an input to
 * `makePrimitiveReservation`, which fires when the booker leaves the cart. By the time the
 * payment step renders, the reservation is already held and the intent can no longer be
 * changed without re-making it.
 *
 * Codes are checked WHEN APPLIED, not when the booker continues. `evaluatePromotion` is
 * read-only: it reserves nothing, creates no guest record, and can be run as often as the
 * booker edits the field.
 *
 * A refused code does NOT block the booking. It shows the service's reason and Continue
 * stays enabled at the undiscounted price, because a promotion is an optional saving and
 * failing the reservation over one would be a worse outcome than not having it. The reason
 * shown is `reasonText`, straight from the service and already translated — the service
 * distinguishes roughly eighteen reasons and every client-side table collapses them.
 *
 * ─── Why this section has no "apply automatically" checkbox ───────────────────────────
 *
 * It had one, and the checkbox was the bug. Promotions come in exactly two kinds:
 * `auto_applied`, which the property grants outright, and `code_entry`, which needs a code.
 * The advertised list is hard-gated to `code_entry` (`AdvertisementAllowed`), and the
 * checkbox applied only `auto_applied` — TWO DISJOINT SETS. But the checkbox sat directly
 * above the list, using the same word ("available") for both, in the select-all-then-items
 * idiom. Ticking it dimmed the list, which reads as "these are handled for you" and meant
 * "these are now unreachable".
 *
 * So an operator ticked a box labelled "apply the best available offer", saw an offer listed
 * under it, and got nothing — twice reported, as "nothing is applied" and as "the Apply
 * button doesn't work". Worse, `applyAutomatic: true` was the RESTING state, so the section
 * arrived with its body greyed out and the only route to it was unticking something that
 * reads as declining a discount on the guest's behalf.
 *
 * The checkbox is gone rather than relabelled. An auto-applied promotion is a pricing rule
 * the property already committed to; the operator has neither the information to judge it
 * per-guest nor the authority to decline it. What was a control is now status
 * (`PropertyDiscounts`), and nothing in the section is ever dimmed — the server has always
 * accepted `applyAutomatic` and `code` together, so there was never a reason to make them
 * compete for one slot.
 *
 * If "book at the undiscounted rate" is a real operator need, it is a different feature with
 * a different shape — a reason-coded, role-gated override — not a checkbox on this card.
 */
const PromotionSection = ({
  intent,
  setIntent,
  cartID,
  cartItems,
  disabled = false,
  cartKey = cartID,
  onValidatedCodeChange
}: PromotionSectionProps) => {
  const classes = useStyles()

  const { offers, loading, failed: offersFailed } = useAdvertisedOffers(cartID)
  const controls = usePromotionIntent(intent, setIntent)
  const verdict = usePromotionEvaluation(cartID, intent, cartKey)
  const property = usePropertyDiscounts(cartID, cartKey)

  useEffect(() => {
    onValidatedCodeChange?.(
      verdict.validatedCode
        ? { code: verdict.validatedCode, cartKey }
        : undefined
    )
  }, [cartKey, onValidatedCodeChange, verdict.validatedCode])

  /*
   * The total comes from whichever evaluation is CURRENTLY valid, never from a sum here.
   *
   * A checked code's verdict wins while it holds, because its total already includes the
   * property's own discounts — the service returns the whole cart, not a delta. When the
   * code is removed or edited, `verdict.totalDiscountMinor` goes undefined by design and
   * the codeless figure takes over, rather than the removed code's total lingering.
   *
   * Never `a + b`: client-side arithmetic on minor units produced a hundredfold
   * overstatement here once already, and a total that disagrees with the payment step is
   * worse than no total.
   */
  const totalMinor = verdict.totalDiscountMinor ?? property.totalDiscountMinor
  const totalCurrency = verdict.currency ?? property.currency

  return (
    <div className={classes.card}>
      {/* "Discounts", one noun. The previous title was "Offers & Discounts" — two English
          synonyms joined by "and" to name one thing, which in Persian becomes
          "تخفیف‌ها و تخفیف‌ها" or silently loses half its meaning. */}
      <Typography variant="subtitle1" className={classes.title}>
        {getTranslate('Discounts')}
      </Typography>

      <PropertyDiscounts
        applied={property.automatic}
        failed={property.failed}
      />

      <OfferList
        offers={offers}
        selectedCode={intent.code}
        disabled={disabled}
        loading={loading}
        failed={offersFailed}
        onSelect={controls.selectOffer}
        onSelectNone={controls.clear}
      />

      {/* The applied row REPLACES the field once a code has been accepted. Two states,
          never both: an editable input beside "applied" invites an edit that silently
          invalidates the verdict shown next to it. */}
      {verdict.appliedForCode ? (
        <AppliedOffer
          applied={verdict.appliedForCode}
          code={(intent.code ?? '').trim()}
          cartItems={cartItems}
          disabled={disabled}
          onRemove={controls.clear}
        />
      ) : (
        <CodeEntry
          code={intent.code ?? ''}
          disabled={disabled}
          checking={verdict.checking}
          onChange={controls.setCode}
          onApply={() => verdict.check((intent.code ?? '').trim())}
        />
      )}

      <PromotionVerdict
        refusal={verdict.refusal}
        failed={verdict.failed}
        noChange={verdict.noChange}
      />

      {/* The only line that answers "what is the discount on this booking".
          Straight from the service — never summed here. Client-side arithmetic on minor
          units is what produced a hundredfold overstatement once already, and a total that
          disagrees with the payment step is worse than no total. */}
      {totalMinor !== undefined &&
        totalCurrency &&
        !/^0+$/.test(totalMinor) &&
        totalMinor !== verdict.appliedForCode?.discountMinor && (
          <div className={classes.totalRow}>
            <Typography variant="body1">
              {getTranslate('Total discount')}
            </Typography>
            <Typography variant="body1" className={classes.totalAmount}>
              {isolate(
                `− ${formatPromotionMinorAmount(totalMinor, totalCurrency)}`
              )}{' '}
              {getTranslate(totalCurrency)}
            </Typography>
          </div>
        )}
    </div>
  )
}

export default PromotionSection
